import { useQuery } from '@tanstack/react-query';

import type { LeadListItem } from '@utazzo/shared';

import { listLeads } from '@/features/leads/leads.api';

/**
 * Lazy "operator inbox" fetch for the ⌘K palette. Only runs while the
 * palette is open and only when the operator has cleared the input. Two
 * parallel calls:
 *
 *   - Today's follow-ups:   `?followUpDue=true&limit=5`
 *   - Revert pending:       `?revertPending=true&limit=5`
 *
 * The server already RBAC-scopes both queries - a SALES_AGENT sees only
 * their own leads, a SALES_LEAD sees their direct reports, ADMIN/OPS see
 * everything. So no caller-side scoping is needed.
 */
export const usePaletteInbox = (
  enabled: boolean,
): {
  todaysFollowUps: LeadListItem[];
  revertPending: LeadListItem[];
  isLoading: boolean;
} => {
  const followUps = useQuery({
    queryKey: ['palette-inbox', 'today-followups'],
    queryFn: () =>
      listLeads({
        page: 1,
        pageSize: 5,
        sortDir: 'desc',
        sortBy: 'updatedAt',
        followUpDue: true,
      }),
    enabled,
    staleTime: 60_000,
    gcTime: 120_000,
  });
  const revert = useQuery({
    queryKey: ['palette-inbox', 'revert-pending'],
    queryFn: () =>
      listLeads({
        page: 1,
        pageSize: 5,
        sortDir: 'desc',
        sortBy: 'updatedAt',
        revertPending: true,
      }),
    enabled,
    staleTime: 60_000,
    gcTime: 120_000,
  });
  return {
    todaysFollowUps: followUps.data?.data ?? [],
    revertPending: revert.data?.data ?? [],
    isLoading: followUps.isLoading || revert.isLoading,
  };
};
